import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, TrendingUp, Activity, Clock, User, Check, Bot, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { mockCostData } from '../data/mockData';
import { ConversationCost } from '../types';
import { AppCard } from '../components/base/AppCard';
import { AppButton } from '../components/base/AppButton';
import { AppInput } from '../components/base/AppInput';
import { AppBadge } from '../components/base/AppBadge';
import { ConversationCostDetailPanel } from '../components/ConversationCostDetailPanel';
type CostViewMode = 'per-utente' | 'per-agente' | 'per-conversazione' | 'dettagli';
interface DetailGroup {
  id: string;
  title: string;
  isExpanded: boolean;
  items: Array<{
    label: string;
    value: string | number;
    description?: string;
  }>;
}
export function CostiPage() {
  const navigate = useNavigate();
  const [selectedConversation, setSelectedConversation] = useState<ConversationCost | null>(null);
  const [monthlyLimit, setMonthlyLimit] = useState(mockCostData.globalMonthlyLimitPerUser);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [viewMode, setViewMode] = useState<CostViewMode>('per-agente');
  // Detail groups state
  const [detailGroups, setDetailGroups] = useState<DetailGroup[]>([{
    id: 'provider',
    title: 'Costi per Provider',
    isExpanded: true,
    items: [{
      label: 'OpenAI',
      value: '€125.40',
      description: 'GPT-4 e GPT-3.5'
    }, {
      label: 'Anthropic',
      value: '€12.30',
      description: 'Claude models'
    }, {
      label: 'Google',
      value: '€0.00',
      description: 'Non utilizzato'
    }]
  }, {
    id: 'feature',
    title: 'Costi per Feature',
    isExpanded: true,
    items: [{
      label: 'Generazione testo',
      value: '€98.50',
      description: 'Risposte AI'
    }, {
      label: 'Analisi dati',
      value: '€22.10',
      description: 'Processing e classificazione'
    }, {
      label: 'Ricerca web',
      value: '€8.90',
      description: 'Tool esterni'
    }, {
      label: 'Widget generation',
      value: '€8.20',
      description: 'UI components'
    }]
  }, {
    id: 'time',
    title: 'Distribuzione Temporale',
    isExpanded: false,
    items: [{
      label: 'Settimana 1',
      value: '€32.40'
    }, {
      label: 'Settimana 2',
      value: '€38.70'
    }, {
      label: 'Settimana 3',
      value: '€41.20'
    }, {
      label: 'Settimana 4',
      value: '€25.40'
    }]
  }, {
    id: 'model',
    title: 'Costi per Modello',
    isExpanded: false,
    items: [{
      label: 'GPT-4 Turbo',
      value: '€89.30',
      description: 'Modello principale'
    }, {
      label: 'GPT-4.1 Mini',
      value: '€36.10',
      description: 'Task semplici'
    }, {
      label: 'GPT-3.5 Turbo',
      value: '€12.30',
      description: 'Fallback'
    }]
  }]);
  const totalCurrentMonth = mockCostData.costPerAgent.reduce((sum, agent) => sum + agent.totalCostEur, 0);
  const mostExpensiveAgent = mockCostData.costPerAgent[0];
  const handleApplyLimit = () => {
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
  };
  const toggleDetailGroup = (groupId: string) => {
    setDetailGroups(groups => groups.map(group => group.id === groupId ? {
      ...group,
      isExpanded: !group.isExpanded
    } : group));
  };
  const handleUserClick = (userId: string) => {
    // Navigate to user detail page (using /utenti for now as placeholder)
    navigate(`/utenti`);
  };
  const handleAgentClick = (agentId: string) => {
    // Navigate to agent detail page
    navigate(`/app-ai/${agentId}/parametri`);
  };
  return <div className="pb-12 space-y-8" data-id="element-1216">
      {/* Success Toast */}
      {showSuccessToast && <div className="fixed top-6 right-6 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-3 rounded-xl shadow-lg flex items-center z-50 animate-fade-in text-sm font-medium" data-id="element-1217">
          <Check className="w-4 h-4 mr-2" data-id="element-1218" />
          Limite aggiornato
        </div>}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1219">
        <AppCard className="flex items-center justify-between p-6" data-id="element-1220">
          <div data-id="element-1221">
            <p className="text-3xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight" data-id="element-1222">
              €{totalCurrentMonth.toFixed(2)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1223">
              Costo totale mese
            </p>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl" data-id="element-1224">
            <DollarSign className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-1225" />
          </div>
        </AppCard>

        <AppCard className="flex items-center justify-between p-6" data-id="element-1226">
          <div data-id="element-1227">
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate" data-id="element-1228">
              {mostExpensiveAgent.agentName}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1229">
              App AI più costosa (€{mostExpensiveAgent.totalCostEur.toFixed(2)})
            </p>
          </div>
          <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-xl" data-id="element-1230">
            <TrendingUp className="w-6 h-6 text-gray-600 dark:text-gray-400" data-id="element-1231" />
          </div>
        </AppCard>

        <AppCard className="flex items-center justify-between p-6" data-id="element-1232">
          <div data-id="element-1233">
            <p className="text-3xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight" data-id="element-1234">
              {mockCostData.totalApiCalls.toLocaleString()}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1235">
              Chiamate API totali
            </p>
          </div>
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl" data-id="element-1236">
            <Activity className="w-6 h-6 text-green-600 dark:text-green-400" data-id="element-1237" />
          </div>
        </AppCard>
      </div>

      {/* View Mode Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 custom-scrollbar" data-id="element-1238">
        <button onClick={() => setViewMode('per-utente')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${viewMode === 'per-utente' ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-white dark:bg-dark-surface text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-subtle hover:border-gray-300 dark:hover:border-dark-medium'}`} data-id="element-1239">
          Per Utente
        </button>
        <button onClick={() => setViewMode('per-agente')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${viewMode === 'per-agente' ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-white dark:bg-dark-surface text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-subtle hover:border-gray-300 dark:hover:border-dark-medium'}`} data-id="element-1240">
          Per App AI
        </button>
        <button onClick={() => setViewMode('per-conversazione')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${viewMode === 'per-conversazione' ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-white dark:bg-dark-surface text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-subtle hover:border-gray-300 dark:hover:border-dark-medium'}`} data-id="element-1241">
          Per Conversazione
        </button>
        <button onClick={() => setViewMode('dettagli')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${viewMode === 'dettagli' ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-white dark:bg-dark-surface text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-subtle hover:border-gray-300 dark:hover:border-dark-medium'}`} data-id="element-1242">
          Dettagli
        </button>
      </div>

      {/* Per Utente View */}
      {viewMode === 'per-utente' && <>
          <AppCard data-id="element-1243">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6" data-id="element-1244">
              Costi per utente
            </h2>

            <div className="space-y-4" data-id="element-1245">
              {mockCostData.userLimits.map(user => {
            const maxCost = Math.max(...mockCostData.userLimits.map(u => u.currentMonthCostEur));
            const percentage = user.currentMonthCostEur / maxCost * 100;
            const limitPercentage = user.currentMonthCostEur / user.limitEur * 100;
            const isNearLimit = limitPercentage > 80;
            return <div key={user.userId} className="flex items-center group" data-id="element-1246">
                    <button onClick={() => handleUserClick(user.userId)} className="w-48 text-sm font-medium text-gray-700 dark:text-gray-300 truncate hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-left flex items-center gap-2" data-id="element-1247">
                      {user.userLabel}
                      <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1248" />
                    </button>
                    <div className="flex-1 mx-4" data-id="element-1249">
                      <div className="h-2 bg-gray-100 dark:bg-dark-medium rounded-full overflow-hidden" data-id="element-1250">
                        <div className={`h-full rounded-full transition-all duration-500 ease-out ${isNearLimit ? 'bg-amber-500 dark:bg-amber-600' : 'bg-blue-500 dark:bg-blue-600'}`} style={{
                    width: `${percentage}%`
                  }} data-id="element-1251"></div>
                      </div>
                    </div>
                    <div className="w-32 text-right text-sm" data-id="element-1252">
                      <span className="font-semibold text-gray-900 dark:text-gray-100" data-id="element-1253">
                        €{user.currentMonthCostEur.toFixed(2)}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500 mx-1" data-id="element-1254">
                        /
                      </span>
                      <span className="text-gray-500 dark:text-gray-400" data-id="element-1255">
                        €{user.limitEur}
                      </span>
                    </div>
                    <div className="w-20 text-right ml-4" data-id="element-1256">
                      <AppBadge variant={isNearLimit ? 'amber' : 'green'} data-id="element-1257">
                        {limitPercentage.toFixed(0)}%
                      </AppBadge>
                    </div>
                  </div>;
          })}
            </div>
          </AppCard>

          {/* Cost Limits */}
          <AppCard data-id="element-1258">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6" data-id="element-1259">
              Gestione limiti
            </h2>

            <div className="max-w-md" data-id="element-1260">
              <AppInput label="Limite mensile per utente (EUR)" type="number" value={monthlyLimit} onChange={e => setMonthlyLimit(Number(e.target.value))} helperText={`Limite corrente: €${monthlyLimit}/mese`} data-id="element-1261" />

              <div className="mt-4" data-id="element-1262">
                <AppButton onClick={handleApplyLimit} fullWidth data-id="element-1263">
                  Applica limite
                </AppButton>
              </div>

              <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-xl flex items-start" data-id="element-1264">
                <div className="text-amber-600 dark:text-amber-400 mr-3 mt-0.5" data-id="element-1265">
                  ⚠️
                </div>
                <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed" data-id="element-1266">
                  Quando un utente raggiunge il limite, le sue richieste
                  verranno messe in coda o bloccate fino al mese successivo.
                </p>
              </div>
            </div>
          </AppCard>
        </>}

      {/* Per Agente View */}
      {viewMode === 'per-agente' && <AppCard data-id="element-1267">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6" data-id="element-1268">
            Costi per App AI
          </h2>

          <div className="space-y-4" data-id="element-1269">
            {mockCostData.costPerAgent.map(agent => {
          const maxCost = mockCostData.costPerAgent[0].totalCostEur;
          const percentage = agent.totalCostEur / maxCost * 100;
          return <div key={agent.agentId} className="flex items-center group" data-id="element-1270">
                  <button onClick={() => handleAgentClick(agent.agentId)} className="flex items-center w-56 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" data-id="element-1271">
                    <Bot className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-3 flex-shrink-0 group-hover:text-blue-600 dark:group-hover:text-blue-400" data-id="element-1272" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400" data-id="element-1273">
                      {agent.agentName}
                    </span>
                    <ChevronRight className="w-3 h-3 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1274" />
                  </button>
                  <div className="flex-1 mx-4" data-id="element-1275">
                    <div className="h-2 bg-gray-100 dark:bg-dark-medium rounded-full overflow-hidden" data-id="element-1276">
                      <div className="h-full bg-blue-500 dark:bg-blue-600 rounded-full transition-all duration-500 ease-out" style={{
                  width: `${percentage}%`
                }} data-id="element-1277"></div>
                    </div>
                  </div>
                  <div className="w-20 text-right text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-1278">
                    €{agent.totalCostEur.toFixed(2)}
                  </div>
                </div>;
        })}
          </div>
        </AppCard>}

      {/* Per Conversazione View */}
      {viewMode === 'per-conversazione' && <AppCard padding="none" className="overflow-hidden" data-id="element-1279">
          <div className="p-6 border-b border-gray-100 dark:border-dark-subtle" data-id="element-1280">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-1281">
              Dettaglio per conversazione
            </h2>
          </div>

          <div className="overflow-x-auto" data-id="element-1282">
            <table className="w-full" data-id="element-1283">
              <thead className="bg-gray-50 dark:bg-dark-subtle border-b border-gray-100 dark:border-dark-medium" data-id="element-1284">
                <tr data-id="element-1285">
                  <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1286">
                    Data e ora
                  </th>
                  <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1287">
                    App AI
                  </th>
                  <th className="px-6 py-4 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1288">
                    Utente
                  </th>
                  <th className="px-6 py-4 text-right text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-1289">
                    Costo
                  </th>
                  <th className="px-6 py-4 w-20" data-id="element-1290"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-dark-medium" data-id="element-1291">
                {mockCostData.conversationCosts.map(conv => <tr key={conv.id} onClick={() => setSelectedConversation(conv)} className="hover:bg-gray-50 dark:hover:bg-dark-hover cursor-pointer transition-colors duration-150" data-id="element-1292">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1293">
                      <div className="flex items-center text-sm text-gray-900 dark:text-gray-100" data-id="element-1294">
                        <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" data-id="element-1295" />
                        {new Date(conv.timestamp).toLocaleString('it-IT', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-1296">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1297">
                        {conv.agentName}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-1298">
                      <div className="flex items-center" data-id="element-1299">
                        <User className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" data-id="element-1300" />
                        <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1301">
                          {conv.userLabel}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1302">
                      <span className="text-sm font-mono font-medium text-gray-900 dark:text-gray-100" data-id="element-1303">
                        €{conv.costEur.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1304">
                      <AppButton variant="ghost" size="sm" data-id="element-1305">
                        Dettagli
                      </AppButton>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </AppCard>}

      {/* Dettagli View - Expandable Groups */}
      {viewMode === 'dettagli' && <div className="space-y-4" data-id="element-1306">
          {detailGroups.map(group => <AppCard key={group.id} padding="none" className="overflow-hidden" data-id="element-1307">
              <button onClick={() => toggleDetailGroup(group.id)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors" data-id="element-1308">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-1309">
                  {group.title}
                </h3>
                {group.isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400 dark:text-gray-500" data-id="element-1310" /> : <ChevronDown className="w-5 h-5 text-gray-400 dark:text-gray-500" data-id="element-1311" />}
              </button>

              {group.isExpanded && <div className="px-6 pb-6 border-t border-gray-100 dark:border-dark-subtle" data-id="element-1312">
                  <div className="pt-4 space-y-3" data-id="element-1313">
                    {group.items.map((item, idx) => <div key={idx} className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-dark-subtle last:border-0" data-id="element-1314">
                        <div className="flex-1" data-id="element-1315">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1316">
                            {item.label}
                          </p>
                          {item.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-1317">
                              {item.description}
                            </p>}
                        </div>
                        <span className="text-base font-semibold text-gray-900 dark:text-gray-100 ml-4" data-id="element-1318">
                          {item.value}
                        </span>
                      </div>)}
                  </div>
                </div>}
            </AppCard>)}
        </div>}

      {/* Conversation Cost Detail Panel */}
      <ConversationCostDetailPanel conversation={selectedConversation} isOpen={!!selectedConversation} onClose={() => setSelectedConversation(null)} data-id="element-1319" />
    </div>;
}